import { useMemo, useState } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { fmt } from '../../utils/numberFormat';
import { puToVolts } from '../../utils/electricalConstants';

const KIND_LABEL: Record<string, string> = {
  trafostation: 'Trafo',
  line: 'Kabel',
  fuse: 'Sicherung',
  hausanschluss: 'HA',
};

const MIN_VISIBLE_DELTA_PCT = 0.5;
const PAGE = 12;

/**
 * Δ per element between the base state and the scenario — the "Auswirkungen"
 * a planner reads first. Clicking a row selects the element on the scenario map.
 */
export function ImpactsList() {
  const run = useStressTestStore(s => s.run);
  const stressElements = useStressTestStore(s => s.stressElements);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    if (!run) return [];
    return run.impacts.filter(i =>
      i.isLimiting
      || Math.abs(i.loadingDeltaPct ?? 0) >= MIN_VISIBLE_DELTA_PCT
      || Math.abs(i.voltageDeltaPu ?? 0) >= 0.002);
  }, [run]);

  if (!run) return null;

  const visible = expanded ? rows : rows.slice(0, PAGE);
  const zoneNames = run.calculationUnit?.zoneNames ?? {};

  const select = (elementId: string) => {
    const el = stressElements.find(e => e.id === elementId)
      ?? stressElements.find(e => 'segments' in e && (e as { segments?: { id: string }[] }).segments?.some(seg => seg.id === elementId));
    if (el) setSelectedElement(el);
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">Auswirkungen</h3>
          <span className="text-[10px] text-gray-500 dark:text-primary-400">ohne → mit Änderungen</span>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-primary-400">Keine Änderung ≥ {MIN_VISIBLE_DELTA_PCT} Prozentpunkte. Ohne Zubau im Szenario sind beide Frames identisch.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-primary-600">
            {visible.map(i => {
              const isVoltage = i.kind === 'hausanschluss';
              const delta = isVoltage ? (i.voltageDeltaPu ?? 0) : (i.loadingDeltaPct ?? 0);
              const worse = isVoltage ? delta < 0 : delta > 0;
              return (
                <li key={i.elementId}>
                  <button
                    type="button"
                    onClick={() => select(i.elementId)}
                    className="w-full text-left py-1.5 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-primary-600 rounded px-1"
                  >
                    <span className={`shrink-0 text-[10px] px-1 rounded ${i.isLimiting ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-primary-600 dark:text-primary-200'}`}>
                      {KIND_LABEL[i.kind] ?? i.kind}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-xs text-gray-800 dark:text-primary-100" title={i.name ?? i.elementId}>
                      {i.name ?? i.elementId}
                      {i.kind === 'fuse' && i.fuseRatingA != null && <span className="text-gray-400 dark:text-primary-500"> {fmt(i.fuseRatingA, 0)} A</span>}
                      {i.zoneId && <span className="ml-1 text-[10px] px-1 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" title="Nachbarzone im Netzverbund">{zoneNames[i.zoneId] ?? i.zoneId}</span>}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-gray-600 dark:text-primary-300">
                      {isVoltage
                        ? `${fmt(puToVolts(i.voltageWithoutPu ?? 1), 0)} → ${fmt(puToVolts(i.voltageWithPu ?? 1), 0)} V`
                        : `${fmt(i.loadingWithoutPct ?? 0, 0)} → ${fmt(i.loadingWithPct ?? 0, 0)} %`}
                    </span>
                    <span className={`shrink-0 w-14 text-right text-[11px] font-semibold tabular-nums ${worse ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {isVoltage
                        ? `${delta > 0 ? '+' : ''}${fmt(puToVolts(1 + delta) - puToVolts(1), 1)} V`
                        : `${delta > 0 ? '+' : ''}${fmt(delta, 1)} pp`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {rows.length > PAGE && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="mt-1 text-[11px] text-accent-primary hover:underline"
          >
            {expanded ? 'Weniger anzeigen' : `Alle ${rows.length} anzeigen`}
          </button>
        )}
      </div>
    </div>
  );
}
